"use client";

import { cn, Typography } from "@kyan/ui";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

import { InfoLine, type InfoLineProps } from "../InfoLine/InfoLine";
import { Divider } from "./components/Divider";
import { type InfoGroupVariant } from "./InfoGroup.types";

const containerClassName = "flex flex-col gap-1";

export interface InfoGroupProps {
  rows: InfoLineProps[];
  title?: string;
  variant: InfoGroupVariant;
  className?: string;
  showDivider?: boolean;
  isGreek?: boolean;
  isLoading?: boolean;
}

const NormalInfoGroup: React.FC<Omit<InfoGroupProps, "variant">> = ({
  rows,
  title,
  className,
  showDivider = true,
  isGreek,
  isLoading = false,
}) => {
  return (
    <div className={cn(containerClassName, className)}>
      {title && (
        <Typography grotesk size={13} weight="medium" variant="primary">
          {title}
        </Typography>
      )}
      {rows.map((row) => (
        <InfoLine
          {...row}
          isGreek={isGreek}
          key={row.data.label}
          isLoading={isLoading}
        />
      ))}
      {Boolean(showDivider) && <Divider variant="normal" />}
    </div>
  );
};

const ExpandableInfoGroup: React.FC<
  Omit<InfoGroupProps, "variant" | "showDivider">
> = ({ rows, title, className, isLoading, isGreek }) => {
  const [open, setOpen] = useState(false);
  const [firstRow, ...restRows] = rows;

  return (
    <div className={cn(containerClassName, className)}>
      {title && (
        <Typography grotesk size={13} weight="medium" variant="primary">
          {title}
        </Typography>
      )}
      <InfoLine
        {...firstRow}
        isGreek={isGreek}
        isLoading={isLoading}
        key={firstRow.data.label}
      />
      <AnimatePresence>
        {Boolean(open) && (
          <motion.div
            exit={{ height: 0 }}
            initial={{ height: 0 }}
            className="overflow-hidden"
            animate={{ height: "auto" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className={containerClassName}>
              {restRows.map((row) => (
                <InfoLine
                  {...row}
                  isGreek={isGreek}
                  key={row.data.label}
                  isLoading={isLoading}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Divider
        open={open}
        variant="expandable"
        onClick={() => {
          setOpen(!open);
        }}
      />
    </div>
  );
};

export const InfoGroup: React.FC<InfoGroupProps> = (props) => {
  if (props.variant === "normal") {
    return <NormalInfoGroup {...props} />;
  }

  return <ExpandableInfoGroup {...props} />;
};
